import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import OrderTrackingTimeline from '../components/OrderTrackingTimeline';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public order tracking page — accessible without login via the link in the
 * tracking email. Shows the visual timeline + carrier deep-link.
 */
export default function PublicTracking() {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/orders/${orderId}`);
        if (!res.ok) throw new Error('Pedido no encontrado');
        const data = await res.json();
        if (!cancelled) setOrder(data);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-8 max-w-2xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-black mb-6" data-testid="tracking-back-link">
          <ArrowLeft className="w-4 h-4" />
          Volver a la tienda
        </Link>

        <h1 className="text-2xl sm:text-3xl font-bold mb-2" style={{ fontFamily: 'Playfair Display' }}>
          Seguimiento de tu pedido
        </h1>

        {loading && <p className="text-gray-500 mt-8">Cargando información del pedido...</p>}

        {error && (
          <div className="mt-8 bg-red-50 border border-red-200 p-6 rounded text-center" data-testid="tracking-error">
            <AlertCircle className="w-10 h-10 mx-auto text-red-400 mb-3" />
            <p className="font-medium text-red-800">{error}</p>
            <p className="text-sm text-gray-600 mt-2">Comprueba que el enlace del email es correcto.</p>
          </div>
        )}

        {order && (
          <>
            <p className="text-sm text-gray-600 mb-6">
              Pedido <span className="font-mono font-bold">#{orderId.substring(0, 8).toUpperCase()}</span>
              {' · '}
              {new Date(order.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' })}
            </p>

            <OrderTrackingTimeline order={order} />

            <div className="mt-6 bg-white border border-gray-200 p-5 rounded text-sm">
              <p className="font-bold mb-2">Resumen del envío</p>
              <p className="text-gray-700">{order.user_name}</p>
              <p className="text-gray-600">{order.user_address}</p>
              <p className="text-gray-600">{order.user_postal_code} {order.user_city}</p>
              <p className="text-gray-600">{order.user_country}</p>
            </div>

            <p className="text-xs text-gray-500 mt-6 text-center">
              ¿Problemas con tu envío? Escríbenos a <a href="mailto:info@arthurfitz.com" className="underline">info@arthurfitz.com</a>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
